import { Icon } from '@zoom/components';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';

import { Separator } from '../../../../components/layout/Separator';
import { EstimatedServiceLevelPerQueue } from '../../../../graphql/components';
import { RecordBooleanType } from '../../../../types';
import { SCHEDULED_STAFF_COLOR } from '../../config';
import { QueuesWithColors } from '../../types';

export interface SLAChartLegendProps {
  dataPerQueueVisibility: RecordBooleanType;
  estimatedServiceLevelPerQueue?: Record<string, EstimatedServiceLevelPerQueue>;
  isMultiQueueSchedule: boolean;
  t: TFunction;
  queueIds: string[];
  queues: QueuesWithColors;
  toggleQueueVisibility: (queueId: string) => void;
  updateVisibilityForAllQueues: () => void;
}

export const SLAChartLegendItem: React.FC<{
  className?: string;
  color: string;
  children: React.ReactNode;
  dataTestId: string;
  disabledMessage?: string;
  isDisabled: boolean;
  isVisible: boolean;
  toggleVisible?: (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
  queueId: string;
}> = ({
  className = 'scheduling-chart__legend__item',
  color,
  dataTestId,
  disabledMessage,
  isDisabled,
  isVisible,
  children,
  toggleVisible,
  queueId,
}) => {
  const Tag = toggleVisible ? 'button' : 'div';

  const id = `sla-legend-item_${queueId}`;

  return (
    <Tag
      className={clsx(className, { disabled: isDisabled })}
      data-test-id={dataTestId}
      aria-checked={isVisible}
      id={id}
      {...(toggleVisible && {
        onClick: toggleVisible,
        role: 'checkbox',
        type: 'button',
      })}
    >
      <span className="scheduling-chart__legend__item-square">
        <span style={{ backgroundColor: color }} />
      </span>

      <div className="scheduling-chart__legend__item-label">
        {children} {disabledMessage && <Icon kind="warning" size={14} color="#aaaaaa" />}
      </div>

      {disabledMessage && (
        <UncontrolledTooltip delay={0} fade={false} placement="top" target={id}>
          {disabledMessage}
        </UncontrolledTooltip>
      )}
    </Tag>
  );
};

export const SLAChartLegend: React.FC<SLAChartLegendProps> = ({
  dataPerQueueVisibility,
  estimatedServiceLevelPerQueue,
  isMultiQueueSchedule,
  queueIds,
  queues,
  t,
  toggleQueueVisibility,
  updateVisibilityForAllQueues,
}) => {
  const containHiddenQueue = Object.values(dataPerQueueVisibility).find((data) => !data) !== undefined;

  const handleClick = (e: React.MouseEvent) => {
    // blur only when invoked by mouse click
    if (e.nativeEvent.detail && e.currentTarget.contains(document.activeElement)) {
      (document.activeElement as HTMLElement).blur();
    }
  };

  return (
    <div className="scheduling-chart__legend--multiple-queues">
      {containHiddenQueue && (
        <>
          <button
            className="scheduling-chart__legend__button"
            data-test-id="scheduling-chart:legend:show-me-all-button"
            onClick={(e) => {
              handleClick(e);
              updateVisibilityForAllQueues();
            }}
            type="button"
          >
            {t('layout.showMeAll')}
          </button>
          <Separator showBorder />
        </>
      )}
      <div className="scheduling-chart__legend">
        {!isMultiQueueSchedule && (
          <SLAChartLegendItem
            className="scheduling-chart__legend__item--sla"
            color={SCHEDULED_STAFF_COLOR}
            dataTestId="scheduling-chart:legend:sla:current"
            disabledMessage={
              estimatedServiceLevelPerQueue && !estimatedServiceLevelPerQueue[queueIds[0]].series.benchmark.length
                ? t('schedule.slaNotDefined')
                : undefined
            }
            isDisabled={!estimatedServiceLevelPerQueue?.[queueIds[0]].series.benchmark.length}
            isVisible
            queueId="current"
          >
            {t('intraday.charts.legendItems.current')}
          </SLAChartLegendItem>
        )}
        {isMultiQueueSchedule &&
          queueIds.map((queueId) => {
            const includesBenchmarkData = !!estimatedServiceLevelPerQueue?.[queueId].series.benchmark.length;

            return (
              <SLAChartLegendItem
                color={queues[queueId].color}
                dataTestId={`scheduling-chart:legend:item(${queueId})`}
                disabledMessage={
                  // do not render disabled message and warning icon until first ESL data are loaded
                  estimatedServiceLevelPerQueue && !includesBenchmarkData ? t('schedule.slaNotDefined') : undefined
                }
                isDisabled={!includesBenchmarkData}
                isVisible={includesBenchmarkData && dataPerQueueVisibility[queueId]}
                key={queueId}
                toggleVisible={
                  includesBenchmarkData
                    ? (e) => {
                        handleClick(e);
                        toggleQueueVisibility(queueId);
                      }
                    : undefined
                }
                queueId={queueId}
              >
                {queues[queueId].name}
              </SLAChartLegendItem>
            );
          })}
      </div>
      <div className="scheduling-chart__legend flex-nowrap">
        <Separator className="ml-0 mr-3" showBorder />
        <SLAChartLegendItem
          className="scheduling-chart__legend__item--sla"
          color="#6c757d"
          dataTestId="scheduling-chart:legend:sla:benchmark"
          isDisabled={false} // not relevant
          isVisible
          queueId="benchmark"
        >
          {t('intraday.charts.legendItems.benchmark')}
        </SLAChartLegendItem>
      </div>
    </div>
  );
};
